import api from './api';
import { getAxiosErrorMessage } from '../utils/error';

export interface WeChatQrCodeResponse {
  success: boolean;
  data?: {
    qr_code_url: string;
    state: string;
    expires_at: string;
  };
  message?: string;
}

export interface WeChatStatusResponse {
  success: boolean;
  data?: {
    status: 'pending' | 'success' | 'expired';
    created_at?: string;
    user_data?: any;
    user?: any;
    access_token?: string;
    token_type?: string;
  };
  message?: string;
}

export interface WeChatLoginResponse {
  success: boolean;
  data?: {
    user: any;
    access_token: string;
    token_type: string;
  };
  message?: string;
}

export interface WeChatBindResponse {
  success: boolean;
  data?: {
    user: any;
    access_token: string;
    token_type: string;
  };
  message?: string;
}

export interface WeChatBindCurrentUserResponse {
  success: boolean;
  data?: {
    user: any;
  };
  message?: string;
}

class WeChatService {
  /**
   * 生成微信登录二维码
   */
  async generateQrCode(params?: { mode?: 'login' | 'bind' }): Promise<WeChatQrCodeResponse> {
    try {
      const response = await api.post('/auth/wechat/qrcode', params || {});
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '生成二维码失败')
      };
    }
  }

  /**
   * 检查二维码扫描状态
   */
  async checkQrCodeStatus(state: string): Promise<WeChatStatusResponse> {
    try {
      const response = await api.post('/auth/wechat/check-status', { state });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '检查状态失败')
      };
    }
  }

  /**
   * 生成公众号二维码
   */
  async generateMpQrCode(params?: { mode?: 'login' | 'bind' }): Promise<WeChatQrCodeResponse> {
    try {
      const response = await api.post('/auth/wechat/mp/qrcode', params || {});
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '生成二维码失败')
      };
    }
  }

  /**
   * 检查公众号二维码扫描状态
   */
  async checkMpQrCodeStatus(state: string): Promise<WeChatStatusResponse> {
    try {
      const response = await api.post('/auth/wechat/mp/check-status', { state });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '检查状态失败')
      };
    }
  }

  /**
   * 微信登录
   */
  async loginWithWeChat(state: string): Promise<WeChatLoginResponse> {
    try {
      const response = await api.post('/auth/wechat/login', { state });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '微信登录失败')
      };
    }
  }

  /**
   * 绑定微信到现有账户
   */
  async bindWeChatToAccount(params: {
    email: string;
    password: string;
    state: string;
  }): Promise<WeChatBindResponse> {
    try {
      const response = await api.post('/auth/wechat/bind', params);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '绑定微信失败')
      };
    }
  }

  /**
   * 绑定微信到当前登录账户（无需邮箱密码）
   */
  async bindWeChatToCurrentUser(state: string): Promise<WeChatBindCurrentUserResponse> {
    try {
      const response = await api.post('/auth/wechat/bind-current', { state });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '绑定微信失败')
      };
    }
  }

  // 解绑当前用户微信
  async unbindWeChat(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.post('/auth/wechat/unbind-current');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: getAxiosErrorMessage(error, '解绑微信失败')
      };
    }
  }

  /**
   * 轮询检查二维码状态
   */
  async pollQrCodeStatus(
    state: string,
    onStatusChange: (status: WeChatStatusResponse) => void,
    maxAttempts: number = 120, // 最多轮询2分钟 (120 * 1000ms)
    interval: number = 1000 // 每秒检查一次
  ): Promise<void> {
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        onStatusChange({
          success: false,
          message: '二维码已过期，请重新获取'
        });
        return;
      }

      attempts++;

      try {
        const result = await this.checkQrCodeStatus(state);
        onStatusChange(result);

        // 如果状态是成功或过期，停止轮询
        if (result.success && result.data?.status &&
          (result.data.status === 'success' || result.data.status === 'expired')) {
          return;
        }

        // 如果状态仍然是pending，继续轮询
        if (result.success && result.data?.status === 'pending') {
          setTimeout(poll, interval);
        }
      } catch (error) {
        onStatusChange({
          success: false,
          message: '检查二维码状态失败'
        });
      }
    };

    poll();
  }

  /**
   * 生成二维码URL的iframe嵌入代码
   */
  generateQrCodeIframe(
    qrCodeUrl: string,
    width: number = 300,
    height: number = 300
  ): string {
    const baseUrl = 'https://open.weixin.qq.com/connect/qrconnect';
    if (qrCodeUrl.includes(baseUrl)) {
      return `<iframe 
        src="${qrCodeUrl}" 
        width="${width}" 
        height="${height}" 
        frameborder="0" 
        scrolling="no"
        style="border: 1px solid #e5e5e5; border-radius: 8px;">
      </iframe>`;
    }
    return '';
  }

  // 前端回调页面：将 code/state 发送给后端处理
  async completeCallback(code: string, state: string): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      const response = await api.post('/auth/wechat/callback', { code, state });
      return response.data;
    } catch (error: any) {
      return { success: false, message: getAxiosErrorMessage(error, '处理回调失败') };
    }
  }
}

const wechatService = new WeChatService();
export default wechatService;
